import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProduct, createOrder } from '../api';
import { getImageUrl, isImageSrc } from '../utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
    ? '/api'
    : 'http://localhost:5000/api');

function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showOrderForm, setShowOrderForm] = useState(false);

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    setLoading(true);
    const data = await getProduct(id);
    setProduct(data);
    setLoading(false);
  };

  const images = product?.images?.length > 0 
    ? product.images 
    : [product?.image || '🌸'];

  const handleBuy = () => {
    setShowOrderForm(true);
  };

  const handleOrderSubmit = async (orderData) => {
    try {
      const order = await createOrder({
        ...orderData,
        items: [{
          productId: product.id,
          name: product.name,
          price: product.price,
          quantity: quantity
        }],
        totalAmount: product.price * quantity
      });
      navigate(`/order-success/${order.id}`);
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Ошибка при оформлении заказа');
    }
  };

  if (loading) {
    return <div className="loading-page">Загрузка...</div>;
  }

  if (!product) {
    return <div className="loading-page">Товар не найден</div>;
  }

  return (
    <div className="product-page">
      <div className="product-page-container">
        {/* Breadcrumbs */}
        <div className="breadcrumbs">
          <span onClick={() => navigate('/')} className="breadcrumb-link">Каталог</span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">
            {product.category === 'roses' && 'Розы'}
            {product.category === 'peonies' && 'Пионы'}
            {product.category === 'tulips' && 'Тюльпаны'}
            {product.category === 'bouquets' && 'Авторские букеты'}
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{product.name}</span>
        </div>

        <div className="product-detail">
          {/* Image Gallery */}
          <div className="product-gallery">
            <div className="gallery-thumbnails">
              {images.map((img, index) => (
                <div
                  key={index}
                  className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                  onClick={() => setSelectedImage(index)}
                >
                  {isImageSrc(img) ? (
                    <img src={getImageUrl(img)} alt={`Preview ${index + 1}`} />
                  ) : (
                    <span className="thumbnail-emoji">{img}</span>
                  )}
                </div>
              ))}
            </div>
            <div className="gallery-main">
              {isImageSrc(images[selectedImage]) ? (
                <img src={getImageUrl(images[selectedImage])} alt={product.name} />
              ) : (
                <div className="main-emoji">{images[selectedImage]}</div>
              )}
            </div>
          </div>

          {/* Product Info */}
          <div className="product-detail-info">
            <h1>{product.name}</h1>
            
            <div className="product-detail-delivery">
              ✓ Бесплатная доставка по городу
            </div>

            <div className="product-detail-price">
              <div className="quantity-selector">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>−</button>
                <span>{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)}>+</button>
              </div>
              <div className="price-amount">{(product.price * quantity).toLocaleString()} ₽</div>
            </div>

            <button className="buy-now-button" onClick={handleBuy}>
              Купить
            </button>

            <div className="product-detail-description">
              <h3>Описание</h3>
              <p>{product.description}</p>
            </div>

            {product.composition && (
              <div className="product-detail-composition">
                <h3>Состав букета</h3>
                <p>{product.composition}</p>
              </div>
            )}

            {product.dimensions && (
              <div className="product-detail-dimensions">
                <h3>Размеры</h3>
                <p>{product.dimensions}</p>
              </div>
            )}

            {product.flowerCount > 0 && (
              <div className="product-detail-flowers">
                <h3>Количество цветов</h3>
                <p>{product.flowerCount} шт</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Order Form Modal */}
      {showOrderForm && (
        <OrderModal
          product={product}
          quantity={quantity}
          onClose={() => setShowOrderForm(false)}
          onSubmit={handleOrderSubmit}
        />
      )}
    </div>
  );
}

function OrderModal({ product, quantity, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    customerName: '',
    telegram: '',
    city: '',
    address: '',
    wishes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    onSubmit(formData);
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content order-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        <h2>Оформление заказа</h2>

        <div className="order-summary">
          <div className="order-summary-product">
            {isImageSrc(product.images?.[0]) ? (
              <img src={getImageUrl(product.images[0])} alt={product.name} />
            ) : (
              <div className="order-product-emoji">{product.image}</div>
            )}
            <div>
              <h4>{product.name}</h4>
              <p>{product.price.toLocaleString()} ₽ × {quantity}</p>
            </div>
          </div>
          <div className="order-summary-total">
            Итого: <strong>{(product.price * quantity).toLocaleString()} ₽</strong>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="order-form">
          <div className="form-group">
            <label>Ваше имя *</label>
            <input
              type="text"
              name="customerName"
              value={formData.customerName}
              onChange={handleChange}
              required
              placeholder="Введите ваше имя"
            />
          </div>

          <div className="form-group">
            <label>Telegram *</label>
            <input
              type="text"
              name="telegram"
              value={formData.telegram}
              onChange={handleChange}
              required
              placeholder="@your_username"
            />
          </div>

          <div className="form-group">
            <label>Город *</label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
              placeholder="Введите ваш город"
            />
          </div>

          <div className="form-group">
            <label>Адрес доставки *</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              placeholder="Улица, дом, квартира..."
              rows="2"
            />
          </div>

          <div className="form-group">
            <label>Пожелания</label>
            <textarea
              name="wishes"
              value={formData.wishes}
              onChange={handleChange}
              placeholder="Время доставки, пожелания к букету..."
              rows="2"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? 'Оформление...' : '✓ Оформить заказ'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ProductPage;
